import { useEffect, useState } from "react";
import { PotentialMatrixFilters } from "../../../hooks/usePotentialMatrixFilters.hook";
import { MatrixDevelopmentItem } from "../../../types/api/potentialMatrix.type";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../../types/AppTypes";
import { useDebounce } from "use-debounce";
import { useDevelopmentMatrixDetails } from "../../../hooks/potentialMatrix/usePotentialMatrixDevelopment.hook";
import {
  StyledDetailsHeader,
  StyledMatrixDetails,
  StyledMatrixDetailsContent,
} from "./DevelopmentMatrixDetails.styled";
import { MatrixGrid } from "../../../components/MatrixGrid/MatrixGrid";
import { ChipSizeEnum } from "../../../components/core/Chip/Chip";
import { CollaboratorChip } from "../../../components/core/CollaboratorChip/CollaboratorChip";
import { IconManager } from "../../../components/core/IconManager/IconManager";
import { Loader } from "../../../components/core/Loader/Loader";
import { Pagination } from "../../../components/core/Pagination/Pagination";
import SearchInput from "../../../components/core/SearchInput/SearchInput";
import { BodyTyp, TitlesTyp } from "../../../components/Typography/Typography";
import { MATRIX_COLLABORATORS_PAGE_SIZE } from "../../../constants/AppConstants";
import {
  NAME_SURNAME_EMAIL_LABEL,
  COLLABORATORS_LABEL,
  EMPTY_STRING,
  EMPTY_COLLABORATORS_MESSAGE,
  RESULTS_LABEL,
} from "../../../constants/Dictionary";
import { MatrixSizes } from "../../../constants/PotentialMatrixConstants";
import { ReactComponent as CloseIcon } from "../../../icons/close.svg";
import { StyledMessageContainer } from "../PotentialMatrixV3.styled";
import { MessageBox } from "../../../components/MessageBox/MessageBox";
import { getInternalGradeMetaFromLabel } from "../../../utils/potentialMatrixGrade.utils";

type DevelopmentMatrixDetailsProps = {
  item: MatrixDevelopmentItem;
  onClose: () => void;
  filters: PotentialMatrixFilters;
  onClickResume: (collaborator: CollaboratorInfo) => void;
  matrixData: MatrixDevelopmentItem[];
  isAnimatingClose?: boolean;
};

export const DevelopmentMatrixDetails = ({
  item,
  onClose,
  filters,
  onClickResume,
  matrixData,
  isAnimatingClose,
}: DevelopmentMatrixDetailsProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);

  const { data, isFetching } = useDevelopmentMatrixDetails({
    enabled: !!item,
    searchDebouncedValue,
    item,
    pageNumber,
    filters,
  });

  const gradeMeta = getInternalGradeMetaFromLabel(item.internalGrade);
  if (!gradeMeta) return null;

  const getText = () => {
    if (!item) return "";

    const screenLabel = item.screen ?? EMPTY_STRING;
    const internalGradeLabel = gradeMeta?.label ?? EMPTY_STRING;

    return `${COLLABORATORS_LABEL} ${internalGradeLabel} in ${screenLabel}`;
  };

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue, filters]);

  return (
    <StyledMatrixDetailsContent
      $visible={!!item}
      $isAnimatingClose={isAnimatingClose}
    >
      <div className="first-column">
        <div className="matrix-container">
          <MatrixGrid
            data={matrixData}
            size={MatrixSizes.SMALL}
            {...(item && { selectedItem: item })}
          />
        </div>
      </div>
      <StyledMatrixDetails>
        <StyledDetailsHeader>
          <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
            {getText()}
          </TitlesTyp>
          <IconManager onClick={onClose} size={IconSizeEnum.MEDIUM}>
            <CloseIcon />
          </IconManager>
        </StyledDetailsHeader>
        {item ? (
          <>
            <StyledDetailsHeader>
              <BodyTyp fontWeight={FontWeightEnum.MEDIUM} variant="2">
                {isFetching ? "" : `${item.count ?? 0} ${RESULTS_LABEL}`}
              </BodyTyp>
              <div className="search-container">
                <SearchInput
                  value={searchValue}
                  onChange={setSearchValue}
                  label={NAME_SURNAME_EMAIL_LABEL}
                  name={"search"}
                />
              </div>
            </StyledDetailsHeader>
            <Loader loading={isFetching} className="profiles-container">
              {data?.data.length === 0 ? (
                <StyledMessageContainer>
                  <MessageBox message={EMPTY_COLLABORATORS_MESSAGE} />
                </StyledMessageContainer>
              ) : (
                <>
                  <div className="profiles-grid">
                    {data?.data.map((collaboratorCalibration) => {
                      const collaboratorGradeMeta =
                        getInternalGradeMetaFromLabel(
                          collaboratorCalibration.collaborator.internalGrade,
                        );

                      return (
                        <CollaboratorChip
                          key={collaboratorCalibration.collaborator.id}
                          collaborator={collaboratorCalibration.collaborator}
                          size={ChipSizeEnum.BIG}
                          onClick={onClickResume}
                          {...(collaboratorGradeMeta?.color && {
                            borderColor: collaboratorGradeMeta.color,
                          })}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </Loader>
            <Pagination
              current={pageNumber + 1}
              total={data?.pagination.total ?? 0}
              onChange={(pageNumber) => setPageNumber(pageNumber - 1)}
              pageSize={MATRIX_COLLABORATORS_PAGE_SIZE}
            />
          </>
        ) : null}
      </StyledMatrixDetails>
    </StyledMatrixDetailsContent>
  );
};
